import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { ptPT, enUS } from '@mui/material/locale';

// Single source of truth for Gateway Portugal brand tokens.
// To re-theme, update values here only.
const brand = {
  primary: '#1B75BB',
  primaryDark: '#0E4C8A',
  primaryLight: '#E8F1FA',
  topBar: '#005BAB',
  bodyText: '#4A4A4A',
  headingsText: '#333333',
  backgroundDefault: '#F9FAFC', // off-white content area
  backgroundPaper: '#FFFFFF', // cards, sidebar, topbar
  backgroundSubtle: '#F7F9FB',
  borders: '#E0E0E0',
  success: '#2E9E5B',
  warning: '#E0A800',
  error: '#D64545',
  // Fine-tuned topbar tokens (from design analysis)
  topbarBorder: '#EFF0F2',
  breadcrumbParent: '#8C8D8F',
  breadcrumbSeparator: '#A1A1A1',
  breadcrumbActive: '#2A2B2D',
  iconBorder: '#E5E7EB',
  iconGlyph: '#7A7A7A',
  avatarBg: '#5B82A8', // topbar avatar
  nameColor: '#343434', // topbar name
  // Sidebar-specific tokens (from design analysis)
  sidebarBorder: '#F0F2F1',
  sidebarSectionLabel: '#AAABAD',
  navItemInactive: '#60656B',
  navItemInactiveIcon: '#64686B',
  navItemActivePill: '#DDECFB',
  navItemActiveColor: '#345C75',
  navItemBadge: '#DD4957',
  sidebarAvatarBg: '#5E8CB1',
  sidebarNameColor: '#858685',
  sidebarSubtitleColor: '#8F8F8F',
  sidebarHairline: '#F6F6F6',
};

export const createGatewayTheme = (config = {}) => {
  const locale = config.language === 'en' ? enUS : ptPT;

  let theme = createTheme(
    {
      direction: config.direction || 'ltr',
      shape: { borderRadius: 3 },
      palette: {
        common: {
          black: '#000000',
          white: '#FFFFFF',
          paper: brand.backgroundPaper,
          mainBackGround: brand.backgroundPaper,
        },
        primary: {
          main: brand.primary,
          dark: brand.primaryDark,
          light: brand.primaryLight,
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: brand.topBar,
          contrastText: '#FFFFFF',
        },
        success: { main: brand.success, contrastText: '#FFFFFF', light: '#5cb85c', dark: '#1a7a3d' },
        warning: { main: brand.warning, contrastText: '#FFFFFF', light: '#f5c842', dark: '#a87a00' },
        error: { main: brand.error, contrastText: '#FFFFFF', light: '#e07070', dark: '#a03030' },
        text: {
          primary: '#2A2B2D', // darkest — page titles, main content
          secondary: '#60656B', // medium gray — labels, secondary descriptions
          disabled: 'rgba(96,101,107,0.45)',
          hint: 'rgba(96,101,107,0.45)',
        },
        background: {
          default: brand.backgroundDefault, // #F9FAFC – content area
          paper: brand.backgroundPaper, // #FFFFFF – cards, surfaces
        },
        divider: brand.borders,
        // Used by DashboardSidebar border
        navBar: brand.borders,
        // Used by NavItem sx color references
        navItem: {
          primary: brand.navItemInactive,
          secondary: brand.navItemInactiveIcon,
          hover: brand.navItemActivePill,
          active: brand.navItemActiveColor,
          activePill: brand.navItemActivePill,
        },
        // Custom Gateway brand keys for component consumption
        brand: {
          blue: brand.primary,
          blueDark: brand.primaryDark,
          blueLight: brand.primaryLight,
          topBar: brand.topBar,
          success: brand.success,
          warning: brand.warning,
          error: brand.error,
          backgroundSubtle: brand.backgroundSubtle,
          borders: brand.borders,
          // Topbar fine-tune tokens
          topbarBorder: brand.topbarBorder,
          breadcrumbParent: brand.breadcrumbParent,
          breadcrumbSeparator: brand.breadcrumbSeparator,
          breadcrumbActive: brand.breadcrumbActive,
          iconBorder: brand.iconBorder,
          iconGlyph: brand.iconGlyph,
          avatarBg: brand.avatarBg,
          nameColor: brand.nameColor,
          // Sidebar fine-tune tokens
          sidebarBorder: brand.sidebarBorder,
          sidebarSectionLabel: brand.sidebarSectionLabel,
          navItemInactive: brand.navItemInactive,
          navItemInactiveIcon: brand.navItemInactiveIcon,
          navItemActivePill: brand.navItemActivePill,
          navItemActiveColor: brand.navItemActiveColor,
          navItemBadge: brand.navItemBadge,
          sidebarAvatarBg: brand.sidebarAvatarBg,
          sidebarNameColor: brand.sidebarNameColor,
          sidebarSubtitleColor: brand.sidebarSubtitleColor,
          sidebarHairline: brand.sidebarHairline,
        },
      },
      typography: {
        fontFamily: '"Poppins", "Helvetica Neue", Arial, sans-serif',
        // Display — large KPI numbers (visitor counts, metrics)
        display: {
          fontWeight: 700,
          fontSize: '2rem',
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
        },
        // Hero — light, uppercase, airy (marketing/landing use)
        h1: {
          fontWeight: 300,
          fontSize: '2.25rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          lineHeight: 1.2,
        },
        // Page title (matches template h2 = 24px bold)
        h2: {
          fontWeight: 700,
          fontSize: '1.5rem',
          lineHeight: 1.3,
        },
        // Section / card headings
        h3: {
          fontWeight: 700,
          fontSize: '1.125rem',
          lineHeight: 1.4,
        },
        h4: {
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: 1.4,
        },
        h5: {
          fontWeight: 600,
          fontSize: '0.9375rem',
          lineHeight: 1.5,
        },
        h6: {
          fontWeight: 600,
          fontSize: '0.875rem',
          lineHeight: 1.5,
        },
        body1: {
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: 1.6,
        },
        body2: {
          fontWeight: 400,
          fontSize: '0.8125rem',
          lineHeight: 1.6,
        },
        subtitle1: {
          fontSize: '0.875rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
        },
        subtitle2: {
          fontSize: '0.8125rem',
          fontWeight: 500,
        },
        button: {
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'none',
          letterSpacing: '0.02em',
        },
        overline: {
          fontWeight: 600,
          fontSize: '0.6875rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        },
        caption: {
          fontSize: '0.75rem',
          color: brand.bodyText,
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: brand.backgroundDefault,
              color: brand.bodyText,
              margin: 0,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 3,
              boxShadow: 'none',
              fontWeight: 600,
              '&:hover': { boxShadow: 'none' },
            },
            containedPrimary: {
              backgroundColor: brand.primaryDark,
              '&:hover': { backgroundColor: brand.primary },
            },
            contained: {
              '&:hover': { boxShadow: 'none' },
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: brand.backgroundPaper,
              color: brand.breadcrumbActive,
              boxShadow: 'none',
              borderBottom: `1px solid ${brand.topbarBorder}`,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: `1px solid ${brand.borders}`,
              borderRadius: 4,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none' },
            elevation1: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
            elevation2: { boxShadow: '0 2px 6px rgba(0,0,0,0.10)' },
            elevation3: { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${brand.borders}`,
              padding: '10px 16px',
              fontSize: '0.8125rem',
            },
            head: {
              fontWeight: 600,
              backgroundColor: brand.backgroundSubtle,
              color: brand.primaryDark,
              fontSize: '0.8125rem',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:hover': { backgroundColor: brand.primaryLight },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { borderRadius: 4, fontWeight: 500 },
          },
        },
        MuiTextField: {
          defaultProps: { size: 'small' },
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': { borderRadius: 3 },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: { fontSize: '0.875rem' },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: { borderRadius: 3 },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: { borderColor: brand.borders },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: { borderRadius: 2, backgroundColor: brand.primaryLight },
            bar: { borderRadius: 2 },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: 3,
              '&.Mui-selected': {
                backgroundColor: brand.primaryLight,
                color: brand.primaryDark,
              },
              '&:hover': { backgroundColor: brand.primaryLight },
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: { backgroundColor: brand.backgroundDefault },
          },
        },
        MuiTypography: {
          defaultProps: {
            variantMapping: {
              display: 'p',
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: brand.primaryDark,
              fontSize: '0.75rem',
              borderRadius: 3,
            },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              border: `1px solid ${brand.borders}`,
              borderRadius: 4,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: brand.backgroundSubtle,
                borderBottom: `1px solid ${brand.borders}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: brand.primaryDark,
                fontSize: '0.8125rem',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: brand.primaryLight,
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${brand.borders}`,
                fontSize: '0.8125rem',
              },
            },
          },
        },
      },
    },
    locale,
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};

export default createGatewayTheme;
